// Zone land-data layer config. This file is the single home for endpoint,
// field and coverage knowledge — extractors and the public API read from here.
//
// coverageBounds: rough [west, south, east, north] lon/lat box of the dataset's
// coverage, used to skip layers that cannot apply to a zone.

export const SCOTLAND_BOUNDS = [-7.7, 54.6, -0.7, 60.9];
export const ENGLAND_BOUNDS = [-6.5, 49.8, 1.8, 55.9];
// Wales sits inside the England box (shared border), so a Welsh zone also
// matches ENGLAND_BOUNDS — England-only layers are fetched but return no
// features there. Region-specific "with data" resolution happens in
// aggregateLandSummary. See KNOWN LIMITATION note at the foot of this file.
export const WALES_BOUNDS = [-5.5, 51.3, -2.6, 53.6];
// Soilscapes (and ALC as a concept) span England & Wales.
export const ENGLAND_WALES_BOUNDS = [-6.5, 49.8, 1.8, 55.9];

export const ZONE_LAND_LAYERS = [
  {
    id: "soilScotland250k",
    title: "Soil (1:250k)",
    category: "soil",
    kind: "graded",
    protocol: "arcgis",
    // MOVED OFF druid 18/08/2026, to Hutton's own ArcGIS Online copy, because
    // their server has been unreachable host-wide since at least 14/08. Same
    // publisher and same dataset, so a hosting change rather than a data-source
    // change. Verified live: both fields below exist on the replacement.
    url: "https://services8.arcgis.com/GUWZHTCSOJuIKNzF/arcgis/rest/services/Hutton_Soils_250K_SSKIB_OSGB_AGOL_FL/FeatureServer/0",
    classCodeField: "ASSOC",
    classNameField: "MSG13_1", // major soil group
    // The map paints 11 generalised soil types off genSoilTyp, not the finer
    // soil association the panel groups by, so the legend must key on this or
    // it would list classes the map does not colour apart.
    legendClassField: "genSoilTyp",
    scaleDenominator: 250000,
    coverageBounds: SCOTLAND_BOUNDS,
  },
  {
    id: "soilScotland25k",
    title: "Soil (1:25k)",
    category: "soil",
    kind: "graded",
    protocol: "arcgis",
    url: "https://druid.hutton.ac.uk/arcgis/rest/services/Hutton_Soils_25K_Apps/MapServer/0",
    // Headline by soil association name. SOIL_DRAIN (drainage) and other
    // attributes exist; which to surface is an agronomy choice.
    classCodeField: "ASSOC_NAME",
    scaleDenominator: 25000,
    // STILL ON druid, and therefore still unavailable. Checked 18/08/2026 across
    // both Hutton ArcGIS Online orgs, their tile org, the UK Soil Observatory
    // and Cranfield: the 1:25k soil map exists nowhere else. Left pointing here
    // on purpose so it returns by itself when Hutton restore the server.
    coverageBounds: SCOTLAND_BOUNDS,
  },
  {
    // ONE LAYER, replacing lcaScotland250k and lcaScotland50k. Changed
    // 18/08/2026 with the same druid outage. Hutton's ArcGIS Online replacement
    // is a "Hybrid" service that already composites the two scales, carrying
    // 39,313 features surveyed at 1:50k over 5,101 at 1:250k, so one query
    // returns the finest data available for the zone.
    //
    // scaleDenominator stays at the COARSER 250,000 deliberately. Per feature,
    // a `Scale` attribute says which survey it came from, so the split could be
    // restored later by filtering on it; until then, claiming 1:50k for a zone
    // that only has 1:250k coverage would overstate the precision, and
    // understating it is the safe direction for a figure a farmer reads.
    id: "lcaScotland",
    title: "Land capability",
    category: "landCapability",
    kind: "graded",
    protocol: "arcgis",
    url: "https://services8.arcgis.com/GUWZHTCSOJuIKNzF/arcgis/rest/services/Hutton_LCA_Hybrid_FL_WebMerc/FeatureServer/1",
    // LCCODE is a NUMBER (e.g. 3.2) — stringified in the breakdown. `Prime`
    // (Yes/No prime-land flag) exists but is not a class name; surfacing it
    // is an agronomy choice.
    classCodeField: "LCCODE",
    scaleDenominator: 250000,
    coverageBounds: SCOTLAND_BOUNDS,
  },
  {
    id: "nvzScotland",
    title: "Nitrate vulnerable zone",
    category: "nvz",
    kind: "categorical",
    protocol: "arcgis",
    url: "https://maps.gov.scot/server/rest/services/ScotGov/AgricultureEnvironment/MapServer/5",
    classNameField: "nvzname",
    coverageBounds: SCOTLAND_BOUNDS,
  },
  {
    id: "sssiEngland",
    title: "SSSI",
    category: "designation",
    kind: "categorical",
    protocol: "wfs",
    url: "https://environment.data.gov.uk/spatialdata/sites-of-special-scientific-interest-england/wfs",
    typeName: "Sites_of_Special_Scientific_Interest_England",
    classNameField: "name",
    coverageBounds: ENGLAND_BOUNDS,
  },
  {
    id: "sacEngland",
    title: "Special Area of Conservation",
    category: "designation",
    kind: "categorical",
    protocol: "wfs",
    url: "https://environment.data.gov.uk/spatialdata/special-areas-of-conservation-england/wfs",
    typeName: "Special_Areas_of_Conservation_England",
    classNameField: "sac_name",
    coverageBounds: ENGLAND_BOUNDS,
  },
  {
    id: "spaEngland",
    title: "Special Protection Area",
    category: "designation",
    kind: "categorical",
    protocol: "wfs",
    url: "https://environment.data.gov.uk/spatialdata/special-protection-areas-england/wfs",
    typeName: "Special_Protection_Areas_England",
    classNameField: "spa_name",
    coverageBounds: ENGLAND_BOUNDS,
  },
  {
    id: "floodZonesEngland",
    title: "Flood zones (rivers & sea)",
    category: "flood",
    // graded so we can report the worst-case zone (FZ3 > FZ2) and its share of
    // the field, not just presence (BBB-454 flood detail).
    kind: "graded",
    protocol: "wfs",
    url: "https://environment.data.gov.uk/spatialdata/flood-map-for-planning-flood-zones/wfs",
    typeName:
      "dataset-04532375-a198-476e-985e-0579a0a11b47:Flood_Zones_2_3_Rivers_and_Sea",
    classCodeField: "flood_zone", // FZ2 / FZ3
    classNameField: "flood_source",
    coverageBounds: ENGLAND_BOUNDS,
  },

  // --- Wales flood (BBB-454 / BBB-456) ------------------------------------
  // Natural Resources Wales risk-map products (Flood Risk Assessment Wales) via
  // DataMapWales WFS. The `risk` attribute carries "High"/"Medium"/"Low" across
  // rivers, sea and surface water (verified live). Severity mapping lives in
  // aggregateLandSummary. Rivers and sea are separate risk layers upstream, both
  // grouped as "Rivers & sea" in the panel breakdown.
  {
    id: "floodRiversWales",
    title: "Flood risk (rivers)",
    category: "flood",
    kind: "graded",
    protocol: "wfs",
    url: "https://datamap.gov.wales/geoserver/inspire-nrw/wfs",
    typeName: "inspire-nrw:NRW_FLOOD_RISK_FROM_RIVERS",
    classCodeField: "risk",
    coverageBounds: WALES_BOUNDS,
  },
  {
    id: "floodSeaWales",
    title: "Flood risk (sea)",
    category: "flood",
    kind: "graded",
    protocol: "wfs",
    url: "https://datamap.gov.wales/geoserver/inspire-nrw/wfs",
    typeName: "inspire-nrw:NRW_FLOOD_RISK_FROM_SEA",
    classCodeField: "risk",
    coverageBounds: WALES_BOUNDS,
  },
  {
    id: "floodSurfaceWaterWales",
    title: "Surface-water flooding",
    category: "flood",
    kind: "graded",
    protocol: "wfs",
    url: "https://datamap.gov.wales/geoserver/inspire-nrw/wfs",
    typeName: "inspire-nrw:NRW_FLOOD_RISK_FROM_SURFACE_WATER_SMALL_WATERCOURSES",
    classCodeField: "risk",
    coverageBounds: WALES_BOUNDS,
  },

  // --- Scotland flood (BBB-457) -------------------------------------------
  // Align panel detection with the map: SEPA medium-likelihood extents via the
  // official Flood_Maps MapServer (the older ArcGIS Online org only published
  // High). Presence => medium likelihood. Categorical by flood type.
  {
    id: "floodRiverScotland",
    title: "Flood risk (rivers)",
    category: "flood",
    kind: "categorical",
    protocol: "arcgis",
    url: "https://map.sepa.org.uk/server/rest/services/Open/Flood_Maps/MapServer/1",
    coverageBounds: SCOTLAND_BOUNDS,
  },
  {
    id: "floodSurfaceScotland",
    title: "Surface-water flooding",
    category: "flood",
    kind: "categorical",
    protocol: "arcgis",
    url: "https://map.sepa.org.uk/server/rest/services/Open/Flood_Maps/MapServer/4",
    coverageBounds: SCOTLAND_BOUNDS,
  },
  {
    id: "floodCoastalScotland",
    title: "Flood risk (coastal)",
    category: "flood",
    kind: "categorical",
    protocol: "arcgis",
    url: "https://map.sepa.org.uk/server/rest/services/Open/Flood_Maps/MapServer/7",
    coverageBounds: SCOTLAND_BOUNDS,
  },

  // --- England & Wales soil (BBB-454) ------------------------------------
  {
    id: "soilEnglandWales",
    title: "Soil (Soilscapes)",
    category: "soil",
    kind: "graded",
    protocol: "arcgis",
    // Cranfield/LandIS Soilscapes national soil map (1:250k), England & Wales.
    // SOILSCAPE holds the plain-English soil description (e.g. "Freely
    // draining slightly acid loamy soils"). TEXTURE / DRAINAGE / FERTILITY
    // also exist; which to surface is an agronomy choice.
    // LICENCE: Soilscapes use is subject to a Cranfield University agreement —
    // confirm BBB's terms before production (flagged; not legal advice).
    // SS_ID is the Soilscape id used to deep-link each type to its definition
    // page (landis.org.uk/soilsguide/soilscapes.cfm?ssid=<SS_ID>).
    //
    // MOVED OFF THE natmapsoilscapes FeatureServer 20/08/2026. That service
    // answers HTTP 200 with `{"error":{"code":500}}` in the body on every
    // request including its own metadata, naming its own fault as "No Layer or
    // Table was initialized". The LandIS server itself is healthy and so is the
    // MapServer the map draws from, so this is that one service. Same failure
    // shape as the druid outage: a 200 carrying an error, which `arcgisQuery.js`
    // does catch, so the panel reported the layer as errored rather than empty.
    //
    // Soilscapes/soilscapes/MapServer/0 is the replacement. Query-capable, same
    // publisher, same 1:250k dataset, and it keeps SS_ID so the deep links still
    // work. Checked against the Cranfield MapServer the map is drawn from over a
    // Shropshire bbox on 20/08/2026: identical, 20 features and the same 7
    // classes from both, so the panel and the map cannot disagree.
    url: "https://www.landis.org.uk/arcgis/rest/services/Soilscapes/soilscapes/MapServer/0",
    classCodeField: "SOILSCAPE",
    classLinkField: "SS_ID",
    // The map is styled on SOILSCAPE too, so panel and legend agree here.
    legendClassField: "SOILSCAPE",
    scaleDenominator: 250000,
    coverageBounds: ENGLAND_WALES_BOUNDS,
  },

  // --- England / Wales Agricultural Land Classification (BBB-454) ---------
  {
    id: "alcEngland",
    title: "Agricultural land classification",
    category: "landCapability",
    kind: "graded",
    protocol: "arcgis",
    // Natural England Provisional ALC — full national coverage (1:250k).
    // ALC_GRADE is already plain ("Grade 1".."Grade 5"). The Post-1988 survey
    // adds 3a/3b subgrades but only covers resurveyed sites, so Provisional is
    // used for consistent national coverage.
    url: "https://services.arcgis.com/JJzESW51TqeY9uat/ArcGIS/rest/services/Provisional%20Agricultural%20Land%20Classification%20(ALC)%20(England)/FeatureServer/0",
    classCodeField: "ALC_GRADE",
    // TAKE esriJSON AND CONVERT IT OURSELVES, added 20/08/2026 (BBB-454).
    // This service's own f=geojson output discards every interior ring: the
    // Grade 3 polygon arrives as 113 outer parts with 0 holes where the esriJSON
    // has 27 outer rings and 86 holes. Grade 3 then covers the Grade 2 and
    // Grade 4 land sitting inside those holes, which is why the make-up rows
    // totalled 122% and 175% on the two demo Norfolk fields. Measured: turf puts
    // the converted geometry at 2,915 km2 against the service's own Shape__Area
    // of 1,933 km2, a ratio of 1.508, while the two hole-free grades come in at
    // 1.020 and 0.996 and do not overlap each other at all.
    // England & Wales Soilscapes preserves all its holes over the same area, so
    // this is one badly behaved service and the flag stays opt-in.
    geometryFormat: "esriJson",
    scaleDenominator: 250000,
    coverageBounds: ENGLAND_BOUNDS,
  },
  {
    id: "alcWales",
    title: "Agricultural land classification",
    category: "landCapability",
    kind: "graded",
    protocol: "wfs",
    // DataMapWales / Welsh Government Predictive ALC Map 2. `predictive` holds
    // the grade as a bare string ("1".."5", 3a/3b). Normalised to "Grade N"
    // for display in aggregateLandSummary.
    url: "https://datamap.gov.wales/geoserver/inspire-wg/wfs",
    typeName: "inspire-wg:wg_predictive_alc2",
    classCodeField: "predictive",
    scaleDenominator: 250000,
    coverageBounds: WALES_BOUNDS,
  },

  // --- England nitrate vulnerable zones (BBB-454) -------------------------
  {
    id: "nvzEngland",
    title: "Nitrate vulnerable zone",
    category: "nvz",
    kind: "categorical",
    protocol: "arcgis",
    // Environment Agency NVZ 2021 designations. Layer /8 carries the current
    // NVZ_2021 field (layer /0 is empty; the /spatialdata WFS path is
    // WMS-only). Wales has no equivalent queryable NVZ source wired — see
    // KNOWN LIMITATION note below.
    url: "https://services1.arcgis.com/JZM7qJpmv7vJ0Hzx/arcgis/rest/services/NVZ_designations/FeatureServer/8",
    coverageBounds: ENGLAND_BOUNDS,
  },

  // --- Scotland designations (BBB-454) ------------------------------------
  // NatureScot ArcGIS FeatureServers. Categorical (tri-state presence) — the
  // site-name field (NAME) isn't needed for the presence row but is recorded
  // here for future use.
  {
    id: "sssiScotland",
    title: "SSSI",
    category: "designation",
    kind: "categorical",
    protocol: "arcgis",
    url: "https://services1.arcgis.com/LM9GyVFsughzHdbO/arcgis/rest/services/Sites_of_Special_Scientific_Interest/FeatureServer/0",
    coverageBounds: SCOTLAND_BOUNDS,
  },
  {
    id: "sacScotland",
    title: "Special Area of Conservation",
    category: "designation",
    kind: "categorical",
    protocol: "arcgis",
    url: "https://services1.arcgis.com/LM9GyVFsughzHdbO/arcgis/rest/services/Special_Areas_of_Conservation/FeatureServer/0",
    coverageBounds: SCOTLAND_BOUNDS,
  },
  {
    id: "spaScotland",
    title: "Special Protection Area",
    category: "designation",
    kind: "categorical",
    protocol: "arcgis",
    url: "https://services1.arcgis.com/LM9GyVFsughzHdbO/arcgis/rest/services/Special_Protection_Areas/FeatureServer/0",
    coverageBounds: SCOTLAND_BOUNDS,
  },

  // --- Wales designations (BBB-454) ---------------------------------------
  // Natural Resources Wales via DataMapWales GeoServer (WFS). Categorical.
  // Name fields differ per layer (sssi_name / SAC_name / SPA_Name) — recorded
  // for future use; not needed for the presence row.
  {
    id: "sssiWales",
    title: "SSSI",
    category: "designation",
    kind: "categorical",
    protocol: "wfs",
    url: "https://datamap.gov.wales/geoserver/inspire-nrw/wfs",
    typeName: "inspire-nrw:NRW_SSSI",
    coverageBounds: WALES_BOUNDS,
  },
  {
    id: "sacWales",
    title: "Special Area of Conservation",
    category: "designation",
    kind: "categorical",
    protocol: "wfs",
    url: "https://datamap.gov.wales/geoserver/inspire-nrw/wfs",
    typeName: "inspire-nrw:NRW_SAC",
    coverageBounds: WALES_BOUNDS,
  },
  {
    id: "spaWales",
    title: "Special Protection Area",
    category: "designation",
    kind: "categorical",
    protocol: "wfs",
    url: "https://datamap.gov.wales/geoserver/inspire-nrw/wfs",
    typeName: "inspire-nrw:NRW_SPA",
    coverageBounds: WALES_BOUNDS,
  },
];

// KNOWN LIMITATIONS (BBB-454). This block is the record; there is no separate
// document behind it.
//   - England and Wales share a bounding box, so for a Welsh zone the
//     England-only categorical layers (nvzEngland, floodZonesEngland) are
//     fetched and return no features, reading as "none". Graded rows (soil,
//     ALC, flood zones) resolve correctly because aggregateLandSummary prefers
//     the layer that actually returned data. A precise fix needs true nation
//     boundaries rather than bounding boxes.
//   - Wales has no queryable NVZ source wired, so a Welsh zone shows
//     "Not in an NVZ" from empty English data — treat as unverified for Wales.
//   - England surface-water flood (EA RoFSW) is deferred: it is extent-only
//     (no severity band) and extremely fragmented (2000+ features hitting the
//     server record cap for a single field), so it needs a point/GetFeatureInfo
//     approach rather than the polygon-intersect extractor. England flood here
//     is rivers & sea (Flood Zones 2/3) only.
//   - Hutton LCA-250k returns empty GeoJSON (f=geojson) though f=json works;
//     Scotland LCA blanks for zones outside the primary 50k coverage.
